import { useMemo } from 'react';
import {
  CartesianGrid,
  ReferenceLine,
  ResponsiveContainer,
  Scatter,
  ScatterChart,
  Tooltip,
  XAxis,
  YAxis,
  ZAxis,
} from 'recharts';
import { Card, CardContent, CardHeader, ErrorBanner, Spinner } from '@/components/ui';
import { useCardFetch } from '@/hooks/use-card-fetch';
import { useCardWidth } from '@/hooks/use-card-width';
import { teamMetricsApi } from '@/services/team-metrics-api';
import type { ITeamCycleTimeDataPoint } from '@skystone/shared';
import type { IMetricViewFilters, MetricViewIssueType } from '@/hooks/use-metric-view-filters';
import { ChartEmptyState, COLOR_BUG, COLOR_STORY, COLOR_TASK, IssueTypeLegend, matchesIssueTypeFilter } from './shared';
import { CycleTimeScatterSummary } from './CycleTimeScatterSummary';
import { CARD_WIDTH_THRESHOLDS, METRIC_VIEW_CHART_HEIGHT } from '@/lib/metric-view/responsive-thresholds';
import { computeP85 } from '@/lib/metric-view/statistics';

type ChartIssueType = 'Story' | 'Bug' | 'Task';
const CHART_TYPES: readonly ChartIssueType[] = ['Story', 'Bug', 'Task'];
const TYPE_COLOR: Record<ChartIssueType, string> = {
  Story: COLOR_STORY,
  Bug: COLOR_BUG,
  Task: COLOR_TASK,
};

interface IScatterPoint extends ITeamCycleTimeDataPoint {
  timestamp: number;
}

interface ICycleTimeScatterCardProps {
  filters: IMetricViewFilters;
}

function groupByType(points: ITeamCycleTimeDataPoint[]): Record<ChartIssueType, IScatterPoint[]> {
  const groups: Record<ChartIssueType, IScatterPoint[]> = { Story: [], Bug: [], Task: [] };
  for (const p of points) {
    if (!CHART_TYPES.includes(p.issueType as ChartIssueType)) continue;
    const type = p.issueType as ChartIssueType;
    groups[type].push({ ...p, timestamp: new Date(p.completedDate).getTime() });
  }
  return groups;
}

function pickTicks(fromMs: number, toMs: number, count = 6): number[] {
  if (toMs <= fromMs || count < 2) return [fromMs, toMs];
  const step = (toMs - fromMs) / (count - 1);
  const ticks: number[] = [];
  for (let i = 0; i < count; i++) ticks.push(Math.round(fromMs + step * i));
  return ticks;
}

function formatTick(ms: number): string {
  return new Date(ms).toISOString().slice(0, 10);
}

interface ITooltipProps {
  active?: boolean;
  payload?: Array<{ payload: IScatterPoint }>;
}

function ScatterTooltip({ active, payload }: Readonly<ITooltipProps>) {
  if (!active || !payload?.[0]) return null;
  const p = payload[0].payload;
  return (
    <div className="rounded-md border bg-background px-3 py-2 text-xs shadow-md">
      <p className="font-semibold">{p.issueKey}</p>
      <p className="text-muted-foreground">
        {p.issueType} · {p.cycleTimeDays}d
      </p>
    </div>
  );
}

const CARD_DESCRIPTION = 'Scatter by issue type — each dot is a closed item.';

function itemLabelForType(issueType: MetricViewIssueType): string {
  if (issueType === 'story') return 'Stories';
  if (issueType === 'bug') return 'Bugs';
  if (issueType === 'task') return 'Tasks';
  return 'items';
}

/**
 * Scatter scope (PRODOPSAI-596): a single team, a whole product pooled
 * across its teams, or — with neither selected — the entire portfolio.
 * Team wins when both ids are set. Same scatter rendering in every case.
 */
function scopeParamsFor(teamId: string, productId: string): { teamId?: string; productId?: string } {
  if (teamId) return { teamId };
  if (productId) return { productId };
  return {};
}

export function CycleTimeScatterCard({ filters }: Readonly<ICycleTimeScatterCardProps>) {
  const { teamId, productId, from, to, issueType, includeArchived } = filters;
  const epicSelected = issueType === 'epic';
  const { ref, width } = useCardWidth<HTMLDivElement>();
  const useSummary = width > 0 && width < CARD_WIDTH_THRESHOLDS.scatter.summaryLine;

  const { data, loading, error } = useCardFetch(
    () => (epicSelected
      ? Promise.resolve(null)
      : teamMetricsApi.getCycleTime({
        ...scopeParamsFor(teamId, productId),
        from,
        to,
        includeArchived: includeArchived || undefined,
      })),
    [teamId, productId, from, to, epicSelected, includeArchived],
    'Failed to load cycle time scatter.',
  );

  const visiblePoints = useMemo(() => {
    if (!data) return [];
    return data.dataPoints.filter((p) => matchesIssueTypeFilter(p.issueType, issueType));
  }, [data, issueType]);

  const grouped = useMemo(() => groupByType(visiblePoints), [visiblePoints]);

  const p85 = useMemo(
    () => computeP85(visiblePoints.map((p) => p.cycleTimeDays)),
    [visiblePoints],
  );

  const xDomain = useMemo<[number, number]>(
    () => [new Date(from).getTime(), new Date(to).getTime()],
    [from, to],
  );
  const xTicks = useMemo(() => pickTicks(xDomain[0], xDomain[1]), [xDomain]);

  let body: React.ReactNode;
  if (epicSelected) {
    body = <ChartEmptyState message="Epic cycle time is not tracked per issue." />;
  } else if (loading) {
    body = (
      <div
        className="flex items-center justify-center"
        style={{ height: METRIC_VIEW_CHART_HEIGHT }}
      >
        <Spinner />
      </div>
    );
  } else if (error) {
    body = <ErrorBanner message={error} />;
  } else if (useSummary) {
    body = <CycleTimeScatterSummary points={visiblePoints} itemLabel={itemLabelForType(issueType)} />;
  } else if (visiblePoints.length === 0) {
    body = <ChartEmptyState message="No closed issues with cycle time in this period." />;
  } else {
    body = (
      <div style={{ height: METRIC_VIEW_CHART_HEIGHT }}>
        <ResponsiveContainer width="100%" height="100%">
          {/* right margin of 60 reserves space for the P85 reference-line
              label; without it the label is either clipped (position 'right')
              or overlaps the data cluster at the right edge (inside*) */}
          <ScatterChart margin={{ top: 10, right: 60, bottom: 10, left: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
            <XAxis
              type="number"
              dataKey="timestamp"
              domain={xDomain}
              ticks={xTicks}
              tickFormatter={formatTick}
              tick={{ fontSize: 11, fill: 'var(--color-muted-foreground)' }}
              axisLine={{ stroke: 'var(--color-border)' }}
            />
            <YAxis
              type="number"
              dataKey="cycleTimeDays"
              tick={{ fontSize: 11, fill: 'var(--color-muted-foreground)' }}
              axisLine={{ stroke: 'var(--color-border)' }}
              unit=" d"
              allowDecimals={false}
            />
            <ZAxis range={[40, 40]} />
            <Tooltip cursor={{ strokeDasharray: '3 3' }} content={<ScatterTooltip />} />
            {p85 !== null && (
              <ReferenceLine
                y={p85}
                stroke="var(--color-muted-foreground)"
                strokeDasharray="4 4"
                ifOverflow="extendDomain"
                label={{
                  // Render the label in the chart's right margin (outside the
                  // plot) so it can never overlap scatter points, regardless of
                  // data density near the right edge.
                  value: `P85: ${Math.round(p85)}d`,
                  position: 'right',
                  fontSize: 10,
                  fill: 'var(--color-muted-foreground)',
                }}
              />
            )}
            {CHART_TYPES.map((type) =>
              grouped[type].length > 0 ? (
                <Scatter key={type} name={type} data={grouped[type]} fill={TYPE_COLOR[type]} />
              ) : null
            )}
          </ScatterChart>
        </ResponsiveContainer>
      </div>
    );
  }

  return (
    <Card ref={ref}>
      <CardHeader>
        <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Cycle Time</h3>
        <p className="text-[11px] text-muted-foreground">{CARD_DESCRIPTION}</p>
        {!useSummary && <div className="mt-2"><IssueTypeLegend /></div>}
      </CardHeader>
      <CardContent>{body}</CardContent>
    </Card>
  );
}
